import { useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { DUST_NEAR_FADE_END, DUST_NEAR_FADE_START, SOFT_FADE_DISTANCE } from './impactFxMath';

interface SoftUniforms {
  tSoftDepth: { value: THREE.Texture | null };
  uSoftInvResolution: { value: THREE.Vector2 };
  uSoftCameraNear: { value: number };
  uSoftCameraFar: { value: number };
  uSoftFadeDistance: { value: number };
  uSoftHasDepth: { value: number };
  uNearFadeStart: { value: number };
  uNearFadeEnd: { value: number };
}

/**
 * Copy the just-presented scene depth into a sampleable texture.
 *
 * A second `gl.render` of this scene (drei-style pre-pass or overrideMaterial)
 * leaves the default framebuffer empty on SwiftShader, so capture screenshots
 * read as a black canvas. Blitting DEPTH after the real present avoids that.
 * Skipped entirely when `enabled` is false (effect tier `off`).
 */
export function useSceneDepthTexture(enabled: boolean): THREE.DepthTexture | null {
  const { gl, size } = useThree();
  const width = Math.max(1, Math.floor(size.width * gl.getPixelRatio()));
  const height = Math.max(1, Math.floor(size.height * gl.getPixelRatio()));

  const resources = useMemo(() => {
    if (!enabled) return null;
    const depthTexture = new THREE.DepthTexture(width, height);
    depthTexture.format = THREE.DepthFormat;
    depthTexture.type = THREE.UnsignedShortType;
    const target = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthTexture,
      stencilBuffer: false,
    });
    return { target, depthTexture };
  }, [enabled, width, height]);

  useEffect(() => {
    if (!resources) return undefined;
    // Allocate the destination framebuffer once. Do not leave it bound.
    const prev = gl.getRenderTarget();
    gl.setRenderTarget(resources.target);
    gl.setRenderTarget(prev);
    return () => {
      resources.target.dispose();
      resources.depthTexture.dispose();
    };
  }, [gl, resources]);

  useFrame(() => {
    if (!resources) return;
    const ctx = gl.getContext() as WebGL2RenderingContext;
    if (typeof ctx.blitFramebuffer !== 'function') return;
    const dest = (
      gl.properties.get(resources.target) as { __webglFramebuffer?: WebGLFramebuffer } | undefined
    )?.__webglFramebuffer;
    if (!dest) return;
    const prevRead = ctx.getParameter(ctx.READ_FRAMEBUFFER_BINDING);
    const prevDraw = ctx.getParameter(ctx.DRAW_FRAMEBUFFER_BINDING);
    try {
      ctx.bindFramebuffer(ctx.READ_FRAMEBUFFER, null);
      ctx.bindFramebuffer(ctx.DRAW_FRAMEBUFFER, dest);
      ctx.blitFramebuffer(
        0,
        0,
        resources.target.width,
        resources.target.height,
        0,
        0,
        resources.target.width,
        resources.target.height,
        ctx.DEPTH_BUFFER_BIT,
        ctx.NEAREST,
      );
    } catch {
      // Soft fade is presentation-only.
    } finally {
      ctx.bindFramebuffer(ctx.READ_FRAMEBUFFER, prevRead);
      ctx.bindFramebuffer(ctx.DRAW_FRAMEBUFFER, prevDraw);
    }
  });

  return resources?.depthTexture ?? null;
}

/** Billboard dust. Instance color tints; do NOT set vertexColors. */
export function createSoftDustMaterial(map: THREE.Texture): THREE.MeshBasicMaterial {
  const uniforms: SoftUniforms = {
    tSoftDepth: { value: null },
    uSoftInvResolution: { value: new THREE.Vector2(1, 1) },
    uSoftCameraNear: { value: 0.1 },
    uSoftCameraFar: { value: 200 },
    uSoftFadeDistance: { value: SOFT_FADE_DISTANCE },
    uSoftHasDepth: { value: 0 },
    uNearFadeStart: { value: DUST_NEAR_FADE_START },
    uNearFadeEnd: { value: DUST_NEAR_FADE_END },
  };

  const material = new THREE.MeshBasicMaterial({
    map,
    color: '#ffffff',
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });
  material.name = 'demolition-soft-dust';
  material.userData.softUniforms = uniforms;
  material.customProgramCacheKey = () => 'demolition-soft-dust-v5';
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aPuffAlpha;
varying float vPuffAlpha;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vPuffAlpha = aPuffAlpha;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
#include <packing>
varying float vPuffAlpha;
uniform sampler2D tSoftDepth;
uniform vec2 uSoftInvResolution;
uniform float uSoftCameraNear;
uniform float uSoftCameraFar;
uniform float uSoftFadeDistance;
uniform float uSoftHasDepth;
uniform float uNearFadeStart;
uniform float uNearFadeEnd;`,
      )
      .replace(
        '#include <opaque_fragment>',
        `#include <opaque_fragment>
gl_FragColor.a *= vPuffAlpha;
float particleViewZ = perspectiveDepthToViewZ(gl_FragCoord.z, uSoftCameraNear, uSoftCameraFar);
gl_FragColor.a *= smoothstep(uNearFadeStart, uNearFadeEnd, max(-particleViewZ, 0.0));
if (uSoftHasDepth > 0.5) {
  vec2 softUv = gl_FragCoord.xy * uSoftInvResolution;
  float sceneDepth = texture2D(tSoftDepth, softUv).x;
  if (sceneDepth > 0.001 && sceneDepth < 0.999) {
    float sceneViewZ = perspectiveDepthToViewZ(sceneDepth, uSoftCameraNear, uSoftCameraFar);
    float gap = particleViewZ - sceneViewZ;
    gl_FragColor.a *= smoothstep(0.0, uSoftFadeDistance, max(gap, 0.0));
  }
}`,
      );
  };
  return material;
}

export function syncSoftParticleUniforms(
  material: THREE.Material,
  depth: THREE.Texture | null,
  camera: THREE.Camera,
  drawingWidth: number,
  drawingHeight: number,
): void {
  const uniforms = material.userData.softUniforms as SoftUniforms | undefined;
  if (!uniforms) return;
  uniforms.tSoftDepth.value = depth;
  uniforms.uSoftHasDepth.value = depth ? 1 : 0;
  uniforms.uSoftInvResolution.value.set(
    1 / Math.max(drawingWidth, 1),
    1 / Math.max(drawingHeight, 1),
  );
  if (camera instanceof THREE.PerspectiveCamera) {
    uniforms.uSoftCameraNear.value = camera.near;
    uniforms.uSoftCameraFar.value = camera.far;
  }
}
